import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AcademicCycleDTO } from '@/shared/application/dto/AcademicCycleDTO';

/**
 * Academic Cycles Store State Interface
 * Manages global state for academic cycles
 */
interface AcademicCyclesState {
  // Data
  academicCycles: AcademicCycleDTO[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions - Setters
  setAcademicCycles: (cycles: AcademicCycleDTO[]) => void;
  addAcademicCycle: (cycle: AcademicCycleDTO) => void;
  updateAcademicCycle: (id: number, cycle: Partial<AcademicCycleDTO>) => void;
  deleteAcademicCycle: (id: number) => void;

  // Actions - State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Helpers
  getAcademicCycleById: (id: number) => AcademicCycleDTO | undefined;
  getAcademicCyclesByWorkspace: (workspaceId: number) => AcademicCycleDTO[];
  getActiveCycles: (workspaceId?: number) => AcademicCycleDTO[];
  getCurrentCycle: (workspaceId?: number) => AcademicCycleDTO | undefined;
  clearAcademicCycles: () => void;
  reset: () => void;
}

/**
 * Initial state for academic cycles store
 */
const initialState = {
  academicCycles: [],
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Academic Cycles Store
 *
 * Global state management for academic cycles using Zustand.
 * Features:
 * - Persistent storage (localStorage)
 * - CRUD operations
 * - Loading and error states
 * - Helper selectors
 * - Workspace filtering
 *
 * Usage:
 * ```typescript
 * const { academicCycles, isLoading, setAcademicCycles } = useAcademicCyclesStore();
 * ```
 *
 * Note: This store should be populated by use cases, not repositories directly.
 * Follow hexagonal architecture principles.
 */
export const useAcademicCyclesStore = create<AcademicCyclesState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Setters
      setAcademicCycles: (cycles) =>
        set({
          academicCycles: cycles,
          lastFetched: Date.now(),
          error: null,
        }),

      addAcademicCycle: (cycle) =>
        set((state) => ({
          academicCycles: [...state.academicCycles, cycle],
          lastFetched: Date.now(),
        })),

      updateAcademicCycle: (id, updatedCycle) =>
        set((state) => ({
          academicCycles: state.academicCycles.map((cycle) =>
            cycle.id === id ? { ...cycle, ...updatedCycle } : cycle
          ),
          lastFetched: Date.now(),
        })),

      deleteAcademicCycle: (id) =>
        set((state) => ({
          academicCycles: state.academicCycles.filter((cycle) => cycle.id !== id),
          lastFetched: Date.now(),
        })),

      // State Management
      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error, isLoading: false }),

      // Helpers
      getAcademicCycleById: (id) => {
        const state = get();
        return state.academicCycles.find((cycle) => cycle.id === id);
      },

      getAcademicCyclesByWorkspace: (workspaceId) => {
        const state = get();
        return state.academicCycles.filter(
          (cycle) => cycle.workspaceId === workspaceId
        );
      },

      getActiveCycles: (workspaceId) => {
        const state = get();
        let cycles = state.academicCycles.filter(
          (cycle) => cycle.status === true
        );

        if (workspaceId !== undefined) {
          cycles = cycles.filter((cycle) => cycle.workspaceId === workspaceId);
        }

        return cycles;
      },

      getCurrentCycle: (workspaceId) => {
        const state = get();
        const now = new Date();

        let cycles = state.academicCycles.filter((cycle) => {
          const startDate = new Date(cycle.startDate);
          const endDate = new Date(cycle.endDate);
          return cycle.status === true && now >= startDate && now <= endDate;
        });

        if (workspaceId !== undefined) {
          cycles = cycles.filter((cycle) => cycle.workspaceId === workspaceId);
        }

        // Return the first matching cycle (should typically be only one)
        return cycles[0];
      },

      clearAcademicCycles: () =>
        set({
          academicCycles: [],
          lastFetched: null,
        }),

      reset: () => set(initialState),
    }),
    {
      name: 'academic-cycles-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist data, not loading/error states
      partialize: (state) => ({
        academicCycles: state.academicCycles,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

/**
 * Selectors for optimized component rendering
 * Use these to select specific parts of the state
 */
export const academicCyclesSelectors = {
  // Select all academic cycles
  selectAcademicCycles: (state: AcademicCyclesState) => state.academicCycles,

  // Select loading state
  selectIsLoading: (state: AcademicCyclesState) => state.isLoading,

  // Select error state
  selectError: (state: AcademicCyclesState) => state.error,

  // Select academic cycle by ID (returns a selector function)
  selectAcademicCycleById: (id: number) => (state: AcademicCyclesState) =>
    state.academicCycles.find((cycle) => cycle.id === id),

  // Select academic cycles by workspace
  selectAcademicCyclesByWorkspace:
    (workspaceId: number) => (state: AcademicCyclesState) =>
      state.academicCycles.filter((cycle) => cycle.workspaceId === workspaceId),

  // Select active cycles
  selectActiveCycles: (state: AcademicCyclesState) =>
    state.academicCycles.filter((cycle) => cycle.status === true),

  // Select closed cycles
  selectClosedCycles: (state: AcademicCyclesState) =>
    state.academicCycles.filter((cycle) => cycle.status === false),

  // Select active cycles by workspace
  selectActiveCyclesByWorkspace:
    (workspaceId: number) => (state: AcademicCyclesState) =>
      state.academicCycles.filter(
        (cycle) => cycle.status === true && cycle.workspaceId === workspaceId
      ),

  // Select current cycle (active and within date range)
  selectCurrentCycle: (workspaceId?: number) => (state: AcademicCyclesState) => {
    const now = new Date();
    let cycles = state.academicCycles.filter((cycle) => {
      const startDate = new Date(cycle.startDate);
      const endDate = new Date(cycle.endDate);
      return cycle.status === true && now >= startDate && now <= endDate;
    });

    if (workspaceId !== undefined) {
      cycles = cycles.filter((cycle) => cycle.workspaceId === workspaceId);
    }

    return cycles[0];
  },

  // Select cycles by status
  selectCyclesByStatus:
    (status: boolean) => (state: AcademicCyclesState) =>
      state.academicCycles.filter((cycle) => cycle.status === status),

  // Select academic cycles count
  selectAcademicCyclesCount: (state: AcademicCyclesState) =>
    state.academicCycles.length,

  // Select sorted cycles (by start date, most recent first)
  selectSortedCycles: (state: AcademicCyclesState) =>
    [...state.academicCycles].sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    ),

  // Check if data is stale (older than 10 minutes)
  selectIsStale: (state: AcademicCyclesState) => {
    if (!state.lastFetched) return true;
    const tenMinutes = 10 * 60 * 1000;
    return Date.now() - state.lastFetched > tenMinutes;
  },
};
